// bd-deepcontext-ioq0 — generic fetch-based SSE hook. Reused by the
// scheduled-jobs list stream and its sibling beads (queue metrics, simulator).
//
// Native EventSource can't be used here: these endpoints sit behind
// requireAuth (passport-jwt, fromAuthHeaderAsBearerToken), and EventSource has
// no way to set a custom Authorization header. So this mirrors the fetch-based
// SSE pattern already proven in runsStream.ts / analysisApi.analyzeStreaming,
// and adds the auto-reconnect EventSource would otherwise provide for free.
import { useEffect, useRef } from "react";
import { openSseStream, readSseStream } from "./sseStreamCore";

export type EventStreamHandlers = Record<string, (data: any) => void>;

export interface UseEventStreamOptions {
  /** Skip connecting while false (e.g. the consumer isn't visible yet). Default true. */
  enabled?: boolean;
  /** Delay before reconnecting after the stream ends or errors. Default 3000ms. */
  reconnectDelayMs?: number;
}

/**
 * Opens a persistent SSE connection to `url` and dispatches each parsed event
 * to the handler registered for its name in `handlers`. Reconnects
 * automatically (after `reconnectDelayMs`) whenever the stream ends or errors,
 * and tears everything down on unmount.
 */
export function useEventStream(
  url: string,
  handlers: EventStreamHandlers,
  options: UseEventStreamOptions = {},
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const enabled = options.enabled ?? true;
  const reconnectDelayMs = options.reconnectDelayMs ?? 3000;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (cancelled) return;
      controller = new AbortController();

      try {
        const response = await openSseStream(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        await readSseStream(response, ({ event, data }) => {
          handlersRef.current[event]?.(data);
        });
      } catch (error: any) {
        if (error?.name === "AbortError") return;
        console.error("Event stream error:", error);
      }

      if (!cancelled) {
        reconnectTimer = setTimeout(connect, reconnectDelayMs);
      }
    };

    connect();

    return () => {
      cancelled = true;
      controller?.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [url, enabled, reconnectDelayMs]);
}
